import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useInvertedCursor } from '../../hooks/useInvertedCursor';
import { SEO } from '../../components/SEO';
import './PrivacyPolicy.css';

const FadeInSection = ({ children, className = '', delay = '' }) => {
    const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });
    return (
        <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className} ${delay}`}>
            {children}
        </div>
    );
};

const PrivacyPolicy = () => {
    useInvertedCursor();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <SEO 
                title="Privacy Policy | MAD OVER TECHNOLOGY"
                description="Privacy Policy for madovertech.io. Learn how we collect, use, and protect your information."
                keywords="Privacy Policy, Data Protection, Privacy, MAD OVER TECHNOLOGY"
                url="/privacy-policy"
            />
            <main className="legal-page">
                <section className="legal-hero">
                    <div className="container mx-auto px-4">
                        <FadeInSection>
                            <h1 className="legal-title">Privacy Policy</h1>
                            <p className="legal-subtitle">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </FadeInSection>
                    </div>
                </section>

                <section className="legal-content">
                    <div className="container mx-auto px-4 max-w-4xl">
                        <FadeInSection>
                            <div className="legal-text">
                                <p>MAD OVER TECHNOLOGY ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your information when you visit madovertech.io.</p>

                                <h2>1. Information We Collect</h2>
                                
                                <h3>a. Information You Provide</h3>
                                <ul>
                                    <li>Name</li>
                                    <li>Email address</li>
                                    <li>Payment details (handled securely by Paddle)</li>
                                    <li>Contact information</li>
                                    <li>Messages you send us</li>
                                </ul>

                                <h3>b. Automatically Collected Data</h3>
                                <ul>
                                    <li>IP address</li>
                                    <li>Device information</li>
                                    <li>Browser type</li>
                                    <li>Usage data (pages visited, time spent, etc.)</li>
                                    <li>Cookies and tracking technologies</li>
                                </ul>

                                <h2>2. How We Use Your Information</h2>
                                <p>We use your information to:</p>
                                <ul>
                                    <li>Provide and improve our services</li>
                                    <li>Process payments and subscriptions</li>
                                    <li>Send important updates or support messages</li>
                                    <li>Personalize user experience</li>
                                    <li>Comply with legal and tax obligations</li>
                                </ul>

                                <h2>3. Cookies</h2>
                                <p>We use cookies for analytics, functionality, performance, and personalization.</p>
                                <p>You can disable cookies in your browser settings.</p>

                                <h2>4. Payment Processing</h2>
                                <p>All payments are processed by Paddle, who acts as our Merchant of Record.</p>
                                <ul>
                                    <li>Paddle may collect your payment information to process transactions securely.</li>
                                    <li>We do NOT store or have access to your full payment card details.</li>
                                </ul>

                                <h2>5. Data Sharing</h2>
                                <p>We do not sell your data.</p>
                                <p>We only share data with:</p>
                                <ul>
                                    <li>Payment processors (Paddle)</li>
                                    <li>Analytics services (like Google Analytics)</li>
                                    <li>Service providers assisting our operations</li>
                                </ul>

                                <h2>6. Data Security</h2>
                                <p>We use industry-standard security measures to protect your data.</p>
                                <p>However, no online transmission is 100% secure.</p>

                                <h2>7. Your Rights</h2>
                                <p>Depending on your region, you may have the right to:</p>
                                <ul>
                                    <li>Access your data</li>
                                    <li>Update or delete information</li>
                                    <li>Request export of data</li>
                                    <li>Opt out of marketing emails</li>
                                </ul>
                                <p>To exercise your rights, contact:</p>
                                <p><a href="mailto:hello@madovertech.io">hello@madovertech.io</a></p>

                                <h2>8. Updates to This Policy</h2>
                                <p>We may update this Privacy Policy periodically.</p>
                                <p>We encourage you to review it regularly.</p>
                            </div>
                        </FadeInSection>
                    </div>
                </section>

                {/* Footer */}
                <footer className="footer">
                    <div className="footer-links">
                        <Link to="/">Home</Link>
                        <Link to="/ai-photobooth">AI Photo Booth</Link>
                        <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer">NexaSync</a>
                        <Link to="/terms-of-service">Terms of Service</Link>
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <Link to="/refund-policy">Refund Policy</Link>
                        <a href="https://www.instagram.com/madovertechnology" target="_blank" rel="noopener noreferrer">Instagram</a>
                        <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20services" target="_blank" rel="noopener noreferrer">Contact</a>
                    </div>
                    <div className="footer-locations">
                        <span>DELHI | BENGALORE | MUMBAI</span>
                    </div>
                    <div className="footer-contact">
                        <a href="mailto:hello@madovertech.io" className="footer-email">hello@madovertech.io</a>
                        <span className="footer-separator">|</span>
                        <a href="tel:+919711689861" className="footer-phone">+91 9711689861</a>
                    </div>
                    <p className="footer-copyright">© 2025 Mad Over Technology. All rights reserved.</p>
                </footer>
            </main>
        </>
    );
};

export default PrivacyPolicy;

